'use client';

import { useEffect, useState } from 'react';

function Tier({ tier, promoted }) {
  const [bg, fg, text] =
    tier === 1 && promoted ? ['bg-graviome-amber/20', 'text-graviome-amber', 'PROMOTED'] :
    tier === 1 ? ['bg-graviome-blue/20', 'text-graviome-blue', 'TIER 1'] :
    ['bg-graviome-grey/30', 'text-graviome-muted', 'TIER 2'];
  return <span className={`text-[10px] px-1.5 py-0.5 rounded font-mono ${bg} ${fg}`}>{text}</span>;
}

function fmt(n) {
  if (n == null) return '—';
  const v = Number(n);
  if (v >= 1e6) return (v / 1e6).toFixed(1) + 'M';
  if (v >= 1e3) return (v / 1e3).toFixed(1) + 'K';
  return String(v);
}

function EdgeList({ title, items, onOpen }) {
  return (
    <div>
      <h3 className="text-[11px] uppercase tracking-widest text-graviome-muted mb-2">{title} <span className="text-graviome-muted/60">({items.length})</span></h3>
      {items.length === 0 ? (
        <div className="text-xs text-graviome-muted font-mono italic">none</div>
      ) : (
        <ul className="space-y-1">
          {items.map(it => (
            <li key={it.username}>
              <button
                onClick={() => onOpen(it.username)}
                className="w-full text-left flex items-center gap-2 text-xs py-1 px-2 rounded hover:bg-graviome-bg-2 transition"
              >
                <Tier tier={it.tier} promoted={it.promoted} />
                <span className="font-mono truncate flex-1">@{it.username}</span>
                <span className="font-mono text-graviome-muted">{it.weight}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ExternalLinkIcon({ className = '' }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className={className} fill="none"
         stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 3h7v7" />
      <path d="M10 14 21 3" />
      <path d="M19 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h6" />
    </svg>
  );
}

export default function AccountDetail({ username, onClose, onOpen, onMutate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true); setError(null); setData(null);
    fetch(`/api/accounts/${encodeURIComponent(username)}`)
      .then(r => r.ok ? r.json() : r.json().then(j => Promise.reject(j)))
      .then(setData)
      .catch(e => setError(e?.error || 'error'))
      .finally(() => setLoading(false));
  };
  useEffect(() => { if (username) load(); }, [username]); // eslint-disable-line

  const toggleBlocked = async () => {
    if (!data?.account) return;
    setBusy(true);
    try {
      await fetch(`/api/accounts/${encodeURIComponent(username)}/block`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ blocked: !data.account.blocked }),
      });
      load();
      onMutate?.();
    } finally { setBusy(false); }
  };

  if (!username) return null;

  return (
    <aside className="w-96 shrink-0 border-l border-graviome-border bg-graviome-panel flex flex-col h-full">
      <div className="p-4 border-b border-graviome-border flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            {data?.account && <Tier tier={data.account.tier} promoted={data.account.promoted} />}
            <div className="text-sm font-semibold truncate">{data?.account?.display_name || username}</div>
          </div>
          <div className="text-xs text-graviome-muted font-mono mt-0.5 truncate">@{username}</div>
        </div>
        <button
          onClick={onClose}
          aria-label="close"
          className="text-graviome-muted hover:text-graviome-fg p-1 -mt-1 -mr-1"
        >
          <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-5">
        {loading && <div className="text-xs text-graviome-muted font-mono">loading…</div>}
        {error === 'not_found' && <div className="text-xs text-graviome-muted font-mono">not in database yet (discovered via mention, not scraped)</div>}
        {error && error !== 'not_found' && <div className="text-xs text-red-400 font-mono">error: {error}</div>}

        {data?.account && (
          <>
            {data.account.bio && (
              <div className="text-xs text-graviome-fg/90 leading-relaxed whitespace-pre-wrap">{data.account.bio}</div>
            )}

            {data.account.last_tweet_text && (
              <div className="border border-graviome-border/60 rounded p-3 bg-graviome-bg/40">
                <div className="flex items-center justify-between mb-1.5">
                  <div className="text-[10px] uppercase tracking-widest text-graviome-muted">last tweet</div>
                  <div className="text-[10px] text-graviome-muted font-mono">
                    {data.account.last_tweet_at ? new Date(data.account.last_tweet_at).toLocaleString() : ''}
                  </div>
                </div>
                <div className="text-xs text-graviome-fg/90 leading-relaxed whitespace-pre-wrap">
                  {data.account.last_tweet_text}
                </div>
                {data.account.last_tweet_id && (
                  <a
                    href={`https://x.com/${username}/status/${data.account.last_tweet_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 mt-2 text-[10px] text-graviome-muted hover:text-graviome-fg font-mono"
                  >
                    <ExternalLinkIcon className="w-2.5 h-2.5" />
                    view on x.com
                  </a>
                )}
              </div>
            )}

            <div className="grid grid-cols-2 gap-2 text-xs font-mono">
              <Stat label="followers" value={fmt(data.account.followers_count)} />
              <Stat label="following" value={fmt(data.account.following_count)} />
              <Stat label="in-edges" value={data.incoming.length} />
              <Stat label="out-edges" value={data.outgoing.length} />
            </div>

            <div className="text-[10px] text-graviome-muted font-mono">
              last scraped: {data.account.last_scraped_at ? new Date(data.account.last_scraped_at).toLocaleString() : 'never'}
            </div>

            <div className="flex flex-wrap gap-2">
              <a
                href={`https://x.com/${username}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-[11px] px-2 py-1.5 rounded border border-graviome-border text-graviome-muted hover:text-graviome-fg hover:border-graviome-fg/40 transition font-mono"
              >
                <ExternalLinkIcon className="w-3 h-3" />
                verify on x.com
              </a>
              <button
                onClick={toggleBlocked}
                disabled={busy}
                className={`inline-flex items-center gap-1.5 text-[11px] px-2 py-1.5 rounded border transition font-mono disabled:opacity-50 ${
                  data.account.blocked
                    ? 'border-graviome-amber/60 text-graviome-amber hover:bg-graviome-amber/10'
                    : 'border-graviome-border text-graviome-muted hover:text-graviome-fg hover:border-graviome-fg/40'
                }`}
                title={data.account.blocked
                  ? 'This account is marked as duopoly — unmark to put it back in the graph'
                  : 'Mark this account as duopoly — hides it from the graph and excludes from promotion'}
              >
                {data.account.blocked ? '◆ unmark duopoly' : '◇ mark as duopoly'}
              </button>
            </div>
            {data.account.blocked && (
              <div className="text-[10px] text-graviome-amber/80 font-mono -mt-2">
                marked as duopoly — hidden from graph & excluded from promotion
              </div>
            )}

            <EdgeList title="mentioned by" items={data.incoming} onOpen={onOpen} />
            <EdgeList title="mentions"     items={data.outgoing} onOpen={onOpen} />
          </>
        )}
      </div>
    </aside>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-graviome-bg-2 border border-graviome-border/60 rounded px-2 py-1.5">
      <div className="text-[9px] uppercase tracking-widest text-graviome-muted">{label}</div>
      <div className="text-sm text-graviome-fg">{value}</div>
    </div>
  );
}
